
import { supabase } from "@/integrations/supabase/client";

/**
 * Duplicates a program and returns the new program ID
 */
export const duplicateProgram = async (programId: string) => {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("You must be logged in to duplicate a program");
    }

    // Fetch the program to duplicate
    const { data: program, error: programError } = await supabase
      .from('programs')
      .select('*')
      .eq('program_id', programId)
      .single();

    if (programError || !program) {
      console.error("Error fetching program:", programError);
      throw new Error("Program not found or you don't have permission to duplicate this program");
    }

    // Get program name from the appropriate location
    let programName = 'Unnamed Program';
    
    // Check if name exists directly on the program object
    if (typeof program.name === 'string') {
      programName = program.name;
    } 
    // Try to get name from program_data if it exists and is an object
    else if (program.program_data && 
             typeof program.program_data === 'object' && 
             !Array.isArray(program.program_data) &&
             typeof program.program_data.name === 'string') {
      programName = program.program_data.name;
    }

    // Create a copy of the program with a new ID
    const newProgram = {
      ...program,
      program_id: undefined, // Let Supabase generate a new ID
      name: `${programName} (Copy)`,
      created_at: new Date().toISOString(),
      last_updated: new Date().toISOString(),
    };

    // If there's program_data, update any internal name references
    if (newProgram.program_data && 
        typeof newProgram.program_data === 'object' && 
        !Array.isArray(newProgram.program_data)) {
      
      // Handle potential program_data.name property safely
      let existingDataName = programName;
      
      if (typeof newProgram.program_data.name === 'string') {
        existingDataName = newProgram.program_data.name;
      }
        
      newProgram.program_data = {
        ...newProgram.program_data,
        name: `${existingDataName} (Copy)`
      };
    }

    // Insert the new program
    const { data: newProgramData, error: insertError } = await supabase
      .from('programs')
      .insert(newProgram)
      .select();

    if (insertError) {
      console.error("Error duplicating program:", insertError);
      throw new Error("Failed to duplicate program. Please try again.");
    }

    // Check if the original program has program_data
    if (program.program_id) {
      // Fetch the original program's program_data
      const { data: originalProgramData, error: fetchDataError } = await supabase
        .from('program_data')
        .select('data')
        .eq('program_id', program.program_id)
        .maybeSingle();
        
      if (!fetchDataError && originalProgramData && newProgramData && newProgramData.length > 0) {
        // If original program had program_data, create it for the new program too
        const { error: insertDataError } = await supabase
          .from('program_data')
          .insert({
            program_id: newProgramData[0].program_id,
            data: originalProgramData.data || {}
          });
          
        if (insertDataError) {
          console.error("Error duplicating program_data:", insertDataError);
          // Don't fail the whole operation if just this part fails
        }
      }
    }

    // Create an activity entry for the program duplication
    if (newProgramData && newProgramData.length > 0) {
      const newProgramId = newProgramData[0].program_id;
      const newProgramName = newProgram.name;

      const { error: activityError } = await supabase
        .from('activities')
        .insert({
          user_id: user.id,
          type: 'new_program',
          title: 'Program Duplicated',
          description: `Duplicated program: ${programName}`,
          data: { 
            program_id: newProgramId, 
            original_program_id: programId,
            program_name: newProgramName 
          }
        });

      if (activityError) {
        console.error("Error creating activity log:", activityError);
        // We don't throw here to avoid blocking the program duplication flow
      }

      return newProgramData[0];
    }

    throw new Error("Failed to duplicate program. No data was returned.");
  } catch (error) {
    console.error("Error in duplicateProgram:", error);
    throw error;
  }
};
